import { ExpandedState } from "@tanstack/react-table";
import React, { useCallback, useMemo, useState } from "react";
import { Button, Stack } from "react-bootstrap";

import { DataTable } from "@/components/DataTable";
import { TableWrapper } from "@/components/TableWrapper";
import { PurchasingPriorityCostSummary } from "@/features/purchasing-priority/PurchasingPriorityCostSummary";
import { PurchasingPriorityLabourSummary } from "@/features/purchasing-priority/PurchasingPriorityLabourSummary";
import { BomNode } from "../../queries/tracking-data/bom-hierarchy-generator";
import { bomHierarchyTableColumns, BomHierarchyTableRow } from "./bom-hierarchy-table-columns";
import { Job } from "../../queries/job-by-name";

export const BomHierarchy: React.FC<{ bomHierarchy: BomNode[]; job?: Job }> = ({ bomHierarchy, job }) => {
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const handleExpandAll = useCallback(() => {
    setExpanded(true);
  }, []);
  const handleCollapseAll = useCallback(() => {
    setExpanded({});
  }, []);

  const tableRows = useMemo(() => createTableRows(bomHierarchy), [bomHierarchy]);

  return (
    <>
      <div className="col-auto mb-3">
        <div className="row row-cols-auto gx-3 align-items-center">
          <div>
            <Button size="sm" variant="secondary" onClick={handleExpandAll}>
              Expand All
            </Button>
          </div>
          <div>
            <Button size="sm" variant="secondary" onClick={handleCollapseAll}>
              Collapse All
            </Button>
          </div>
          {job && (
            <Stack gap={1}>
              <Stack direction="horizontal" gap={2}>
                <span className="text-end" style={{ minWidth: "8em" }}>
                  Estimated Costs:
                </span>
                <PurchasingPriorityCostSummary />
              </Stack>
              <Stack direction="horizontal" gap={2}>
                <span className="text-end" style={{ minWidth: "8em" }}>
                  Estimated Labour:
                </span>
                <PurchasingPriorityLabourSummary />
              </Stack>
            </Stack>
          )}
        </div>
      </div>

      <TableWrapper className="col">
        <DataTable
          bordered
          hover
          className="bom-hierarchy-table"
          data={tableRows}
          columns={bomHierarchyTableColumns}
          getSubRows={(x) => x.children}
          state={{ expanded, columnVisibility: { ["purchasingPriority_priority"]: !!job } }}
          onExpandedChange={setExpanded}
          meta={{
            job,
            dataRowProps: (row) => ({
              className: `tree-depth-${row.depth.toFixed(0)} text-nowrap`,
            }),
          }}
        />
      </TableWrapper>
    </>
  );
};

function createTableRows(bomNodes: BomNode[]): BomHierarchyTableRow[] {
  return bomNodes.map((node) => {
    const unitLabourCost = node.categorizedUnitCosts.labour;
    const totalLabourCost = node.categorizedLineItemCosts.labour;
    return {
      ...node,
      unitLabourCost,
      totalLabourCost,
      unitPurchaseCost: node.unitCost.subtract(unitLabourCost),
      totalPurchaseCost: node.lineItemCost.subtract(totalLabourCost),
      children: createTableRows(node.children),
    };
  });
}
